import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signUp,
  resendSignUpCode,
  confirmSignUp,
  signIn,
  signInWithRedirect,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  fetchUserAttributes,
  signOut,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { capture, resetAnalytics } from 'lib/analytics/posthog';

declare global {
  interface Window {
    getIdToken?: () => Promise<string | null>;
  }
}

interface AmplifyUser {
  username: string;
  userId: string;
  signInDetails?: { loginId?: string };
  // enriched fields we add:
  givenName?: string;
  familyName?: string;
  email?: string;
  company?: string;
  groups?: string[];
  attributes?: Record<string, string>;
}

interface AuthContextType {
  user: AmplifyUser | null;
  setUser: React.Dispatch<React.SetStateAction<AmplifyUser | null>>;
  loading: boolean;
  getIdToken: () => Promise<string | null>;
  login: (
    email: string,
    password: string
  ) => Promise<{ status: 'SIGNED_IN' } | { status: 'CONFIRM_ACCOUNT' } | { status: 'NEW_PASSWORD_REQUIRED' }>;
  loginWithGoogle: () => Promise<void>;
  confirmAccount: (email: string, code: string, password: string) => Promise<void>;
  completeNewPassword: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<boolean>;
  registerUser: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    company: string;
  }) => Promise<{ status: 'SIGN_UP_COMPLETE' } | { status: 'CONFIRM_REQUIRED' }>;
  confirmRegistration: (email: string, code: string, password: string) => Promise<void>;
  startPasswordReset: (email: string) => Promise<void>;
  completePasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AmplifyUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const hydrateUser = async () => {
    try {
      const base = await getCurrentUser(); // { username, userId, signInDetails }
      const session = await fetchAuthSession(); // tokens etc.

      // Prefer direct attributes when available
      let attrs: Record<string, string> | undefined;
      try {
        const rawAttrs = await fetchUserAttributes(); // { given_name, family_name, email, 'custom:company', ... }
        attrs = Object.fromEntries(
          Object.entries(rawAttrs || {}).filter(([, value]) => value !== undefined && value !== null)
        ) as Record<string, string>;
      } catch {
        // not signed-in yet or no permission — ignore
      }

      // Fallbacks from ID token payload if attrs missing
      const payload = (session.tokens?.idToken?.payload ?? {}) as Record<string, unknown>;
      const groups = (payload['cognito:groups'] as string[]) || undefined;

      const enriched: AmplifyUser = {
        ...(base as AmplifyUser),
        attributes: attrs,
        givenName: attrs?.given_name ?? (payload['given_name'] as string | undefined),
        familyName: attrs?.family_name ?? (payload['family_name'] as string | undefined),
        email: attrs?.email ?? (payload['email'] as string | undefined),
        company: attrs?.['custom:company'] ?? (payload['custom:company'] as string | undefined),
        groups,
      };

      setUser(enriched);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Token accessor for API client
  const getIdToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  };

  // Make getIdToken globally accessible to api.ts
  if (typeof window !== 'undefined') {
    window.getIdToken = getIdToken;
  }

  // Listen to Cognito auth events
  useEffect(() => {
    hydrateUser();

    const listener = ({ payload }: { payload: { event: string } }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          hydrateUser();
          break;
        case 'signedOut':
          setUser(null);
          break;
        default:
          break;
      }
    };

    const unsubscribe = Hub.listen('auth', listener);
    return () => unsubscribe();
  }, []);

  // Authentication helpers
  const login = async (email: string, password: string) => {
    const result = await signIn({
      username: email,
      password,
    });

    if (result.isSignedIn) {
      await hydrateUser();
      capture('account_signed_in', { auth_method: 'password' });
      return { status: 'SIGNED_IN' as const };
    }

    const step = result.nextStep.signInStep;

    if (step === 'CONFIRM_SIGN_UP') {
      await resendSignUpCode({ username: email });
      return { status: 'CONFIRM_ACCOUNT' as const };
    }

    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { status: 'NEW_PASSWORD_REQUIRED' as const };
    }

    throw new Error('Unexpected authentication step');
  };

  const loginWithGoogle = async () => {
    capture('google_sign_in_started', { auth_method: 'google' });
    await signInWithRedirect({ provider: 'Google' });
  };

  const confirmAccount = async (email: string, code: string, password: string) => {
    await confirmSignUp({
      username: email,
      confirmationCode: code,
    });

    const result = await signIn({
      username: email,
      password,
    });

    if (!result.isSignedIn) {
      throw new Error('Failed to sign in after confirming account');
    }

    await hydrateUser();
    capture('account_confirmed', { auth_method: 'password' });
  };

  const completeNewPassword = async (password: string) => {
    const result = await confirmSignIn({
      challengeResponse: password,
    });

    if (!result.isSignedIn) {
      throw new Error('Failed to complete new password challenge');
    }

    await hydrateUser();
  };

  const logout = async () => {
    await signOut();
    capture('logout_completed');
    resetAnalytics();
    setUser(null);
  };

  const checkSession = async () => {
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.idToken;
    } catch {
      return false;
    }
  };

  const registerUser = async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    company: string;
  }) => {
    const { email, password, firstName, lastName, company } = data;

    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          given_name: firstName,
          family_name: lastName,
          'custom:company': company,
        },
        autoSignIn: true,
      },
    });

    if (result.isSignUpComplete) {
      return { status: 'SIGN_UP_COMPLETE' as const };
    }

    return { status: 'CONFIRM_REQUIRED' as const };
  };

  const confirmRegistration = async (email: string, code: string, password: string) => {
    await confirmAccount(email, code, password);
  };

  const startPasswordReset = async (email: string) => {
    await resetPassword({ username: email });
  };

  const completePasswordReset = async (email: string, code: string, newPassword: string) => {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        getIdToken,
        login,
        loginWithGoogle,
        confirmAccount,
        completeNewPassword,
        logout,
        checkSession,
        registerUser,
        confirmRegistration,
        startPasswordReset,
        completePasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
